import { useRecoilState, useRecoilValue } from 'recoil';
import Drawer from '@mui/material/Drawer';

import { LogEntry } from 'common/graphqlTypes';

import { selectedLogEntryState } from 'api/apolloClient.state';
import { selectedTokenState } from 'components/common/Managers/RouteStateManager.state';
import { getSchemaState, logDetailsRendererState } from 'components/common/Managers/ViewStateManager.state';
import { pinLogDetailsState } from './SearchStateManager.state';

import './InspectFieldsDrawer.scss';

type InspectFieldsDrawerProps = {
  open?:                    boolean;
  close:                   () => void;
  logEntry?:               LogEntry | null;
  continuationToken?:      string;
  enableLogSearchActions?: boolean;
  dataSource?:             string;
};

/**
 * This component displays a single event's field/value pairs, with the server fields
 * listed separately.
 *
 * @prop open                      if true, the dialog is visible
 * @prop close                     function that closes the dialog
 * @prop logEntry                  the currently selected log line whose field/value pairs are shown
 * @prop continuationToken         unique identifier representing the currently selected log line
 * @prop enableLogSearchActions    if true, includes Original Log and Thread Log buttons
 * @prop dataSource                data source
 */
function InspectFieldsDrawer({
  open,
  close,
  logEntry: selectedLogEntry,
  continuationToken: selectedToken,
  enableLogSearchActions = true,
  dataSource,
}: InspectFieldsDrawerProps): JSX.Element | null {

  const [ pinLogDetails, setPinLogDetails ] = useRecoilState(pinLogDetailsState);
  const selectedLogEntryFromState = useRecoilValue(selectedLogEntryState);
  const logEntry = selectedLogEntry || selectedLogEntryFromState;
  const continuationTokenFromState = useRecoilValue(selectedTokenState);
  const continuationToken = selectedToken || continuationTokenFromState;

  const LogDetailsRenderer = useRecoilValue(logDetailsRendererState);
  const getSchema = useRecoilValue(getSchemaState);

  if (pinLogDetails) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      className="InspectFieldsDrawer"
      open={open}
      transitionDuration={{ enter: 200, exit: 50 }}
      variant="persistent"
    >
      <LogDetailsRenderer
        logEntry={logEntry}
        continuationToken={continuationToken}
        onClose={close}
        onTogglePinned={() => setPinLogDetails((pinLogDetails) => !pinLogDetails)}
        isPinned={pinLogDetails}
        enableLogSearchActions={enableLogSearchActions}
        schema={getSchema(logEntry, dataSource)}
      />
    </Drawer>
  );
}

export default InspectFieldsDrawer;
